import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { VectaraApiError, getMemory, updateMemory, type AgentMemory } from "./api";

const POLL_INTERVAL_MS = 4000;
const KEY_SETTLE_MS = 600;
const POLL_ATTEMPTS = 20;

function MemoryContent({ content }: { content: string }) {
  const blocks: ReactElement[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length > 0) {
      blocks.push(
        <ul key={`list-${blocks.length}`}>
          {bullets.map((bullet, index) => (
            <li key={index}>{bullet}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  for (const line of content.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      bullets.push(trimmed.slice(2));
      continue;
    }
    flush();
    if (trimmed.startsWith("#")) {
      blocks.push(<h5 key={`h-${blocks.length}`}>{trimmed.replace(/^#+\s*/, "")}</h5>);
    } else if (trimmed !== "") {
      blocks.push(<p key={`p-${blocks.length}`}>{trimmed}</p>);
    }
  }
  flush();
  return <div className="memory-content">{blocks}</div>;
}

export function MemoryPanel({
  apiKey,
  agentKey,
  completedRuns
}: {
  apiKey: string;
  agentKey: string;
  completedRuns: number;
}) {
  const [memory, setMemory] = useState<AgentMemory | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<{ tone: "ok" | "error" | "pending"; text: string } | null>(null);
  const versionRef = useRef(0);

  const load = useCallback(async (): Promise<AgentMemory | null> => {
    if (apiKey.trim() === "" || agentKey.trim() === "") return null;
    try {
      const loaded = await getMemory(apiKey.trim(), agentKey.trim());
      setMemory(loaded);
      versionRef.current = loaded.version;
      return loaded;
    } catch (error) {
      setStatus({ tone: "error", text: (error as Error).message });
      return null;
    }
  }, [apiKey, agentKey]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), KEY_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (completedRuns === 0) return;
    let cancelled = false;
    const startVersion = versionRef.current;
    setStatus({ tone: "pending", text: "Learning from the last run…" });
    void (async () => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS && !cancelled; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (cancelled) return;
        const loaded = await load();
        if (loaded && loaded.version > startVersion) {
          setStatus({ tone: "ok", text: `Learned from the last run: memory is now version ${loaded.version}.` });
          return;
        }
      }
      if (!cancelled) setStatus({ tone: "ok", text: "Nothing new to remember from the last run." });
    })();
    return () => {
      cancelled = true;
    };
  }, [completedRuns, load]);

  async function save() {
    if (draft === null || memory === null) return;
    try {
      const saved = await updateMemory(apiKey.trim(), agentKey.trim(), draft, memory.version);
      setMemory(saved);
      versionRef.current = saved.version;
      setDraft(null);
      setStatus({ tone: "ok", text: `Saved as version ${saved.version}.` });
    } catch (error) {
      const conflict = error instanceof VectaraApiError && error.status === 409;
      setStatus({
        tone: "error",
        text: conflict ? "The memory changed while you were editing. Refresh and edit again." : (error as Error).message
      });
    }
  }

  return (
    <section className="panel memory">
      <div className="panel-head">
        <h2>What the agent has learned</h2>
        {memory && memory.content.trim() !== "" && <span className="badge">v{memory.version}</span>}
      </div>
      {memory === null ? (
        <p className="hint">Enter an API key to see the agent's memory.</p>
      ) : draft !== null ? (
        <>
          <textarea className="memory-editor" rows={10} value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="actions">
            <button type="button" className="button small primary" onClick={save}>
              Save
            </button>
            <button type="button" className="button small ghost" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          {memory.content.trim() === "" ? (
            <p className="hint">
              Nothing yet. The agent remembers reviewer corrections and its own mistakes after each run.
            </p>
          ) : (
            <MemoryContent content={memory.content} />
          )}
          {memory.lastUpdated && memory.content.trim() !== "" && (
            <p className="hint">
              Updated {new Date(memory.lastUpdated).toLocaleString()} by{" "}
              {memory.lastUpdatedBy === "fact_extraction" ? "the agent" : memory.lastUpdatedBy}
            </p>
          )}
          <div className="actions">
            <button type="button" className="button small" onClick={() => void load()}>
              Refresh
            </button>
            <button type="button" className="button small ghost" onClick={() => setDraft(memory.content)}>
              Edit
            </button>
          </div>
        </>
      )}
      {status && <p className={`status ${status.tone}`}>{status.text}</p>}
    </section>
  );
}
